import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type PrintZone = "page" | "header" | "body" | "sign" | "footer";
const PRINT_ZONES: PrintZone[] = ["page", "header", "body", "sign", "footer"];
const PAGE_W = 794;
const PAGE_H = 1123;

type Props = {
  html: string; title: string; scale?: number | "fit"; activeZone?: PrintZone | null;
  onZoneClick?: (zone: PrintZone) => void; className?: string;
};

// Styles below live inside the printed sheet (iframe), not in the app UI, so literal colors are intended.
const zoneStyle = (active: PrintZone | null | undefined, hover: boolean) => `<style>
${hover ? `[data-zone]:not([data-zone="page"]):hover{outline:1px dashed #D9D3CB;outline-offset:3px;cursor:pointer}` : ""}
${active ? `[data-zone="${active}"]{outline:2px solid #C9492A !important;outline-offset:3px;border-radius:4px}` : ""}
</style>`;

export function DocumentPreview({ html, title, scale = "fit", activeZone, onZoneClick, className }: Props) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);
  const s = scale === "fit" ? fitScale : scale;

  useLayoutEffect(() => {
    if (scale !== "fit" || !boxRef.current) return;
    const initial = boxRef.current.getBoundingClientRect().width;
    if (initial > 0) setFitScale(Math.min(1, initial / PAGE_W));
    const observer = new ResizeObserver(([entry]) => setFitScale(Math.min(1, entry.contentRect.width / PAGE_W)));
    observer.observe(boxRef.current);
    return () => observer.disconnect();
  }, [scale]);

  useEffect(() => {
    if (!onZoneClick) return;
    const handler = (event: MessageEvent) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      const data = event.data as { type?: string; zone?: unknown } | null;
      if (data?.type !== "akuna-print-zone") return;
      if (typeof data.zone !== "string" || !PRINT_ZONES.includes(data.zone as PrintZone)) return;
      onZoneClick(data.zone as PrintZone);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onZoneClick]);

  const srcDoc = useMemo(
    () => (onZoneClick || activeZone ? html.replace("</head>", () => `${zoneStyle(activeZone, !!onZoneClick)}</head>`) : html),
    [html, activeZone, onZoneClick],
  );

  return (
    <div ref={boxRef} className={cn("w-full", className)}>
      {/* Inline style: dynamic scaled dimensions that Tailwind cannot express. */}
      <div className="mx-auto overflow-hidden bg-card shadow-lg" style={{ width: PAGE_W * s, height: PAGE_H * s }}>
        <iframe
          ref={frameRef}
          title={title}
          srcDoc={srcDoc}
          sandbox="allow-scripts"
          className="origin-top-left border-0"
          style={{ width: PAGE_W, height: PAGE_H, transform: `scale(${s})` }}
        />
      </div>
    </div>
  );
}
